import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Sentry from '@sentry/browser';

import { FILTERS, getFilter } from '../core/filters';
import { STICKER_FRAMES, getFrame } from '../core/stickerFrames';
import { filterPhoto, loadPhoto, scalePhoto } from '../platform/photo';
import { composeStory, createFramePreviews } from '../platform/storyImage';

const FILTER_PREVIEW_EDGE = 160;

// "Spring Shred!" becomes "spring-shred-sticker.png".
const toFileName = (challengeName) => {
  const slug = challengeName.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/ /g, '-');
  return `${slug || 'commitment'}-sticker.png`;
};

const EMPTY_STORY = { file: null, url: null, stickerUrl: null };

/**
 * The client's choices for their commitment sticker, the sticker they see, and the story they make from it, kept
 * ready to share.
 *
 * The story is drawn again on every change, so the file already exists when the client taps Share: browsers only
 * allow sharing while a tap is being handled, and drawing during the tap would run past that.
 *
 * @param {import('../core/shareCard').ShareCard} card
 */
const useStoryComposer = (card) => {
  const [photo, setPhoto] = useState(null);
  const [frameId, setFrameId] = useState(STICKER_FRAMES[0].id);
  const [filterId, setFilterId] = useState(FILTERS[0].id);
  const [story, setStory] = useState(EMPTY_STORY);
  const [framePreviews, setFramePreviews] = useState({});
  const [isRendering, setIsRendering] = useState(true);

  const filteredPhoto = useMemo(() => photo && filterPhoto(photo, getFilter(filterId)), [photo, filterId]);

  // A small copy of the photo in each filter, for the filter picker.
  const filterPreviews = useMemo(() => {
    if (!photo) {
      return {};
    }
    const thumbnail = scalePhoto(photo, FILTER_PREVIEW_EDGE);
    return Object.fromEntries(FILTERS.map((filter) => [filter.id, filterPhoto(thumbnail, filter).toDataURL()]));
  }, [photo]);

  useEffect(() => {
    let isCurrent = true;
    setIsRendering(true);
    const scene = {
      card,
      frame: getFrame(frameId),
      photo: filteredPhoto,
      now: new Date(),
    };
    composeStory(scene, toFileName(card.challengeName))
      .then(({ stickerUrl, storyFile }) => {
        if (!isCurrent) {
          URL.revokeObjectURL(stickerUrl);
          return;
        }
        setStory({ file: storyFile, url: storyFile && URL.createObjectURL(storyFile), stickerUrl });
      })
      .catch((error) => Sentry.captureException(error, { extra: { challengeId: card.challengeId, frameId } }))
      .finally(() => {
        if (isCurrent) {
          setIsRendering(false);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [card, frameId, filteredPhoto]);

  // Each object URL holds an image in memory until it is revoked: once replaced, or when the flow closes.
  useEffect(() => () => {
    [story.url, story.stickerUrl].filter(Boolean).forEach((url) => URL.revokeObjectURL(url));
  }, [story]);

  useEffect(() => {
    let isCurrent = true;
    createFramePreviews({ card, photo: filteredPhoto, now: new Date() }, STICKER_FRAMES)
      .then((previews) => {
        if (isCurrent) {
          setFramePreviews(previews);
        }
      })
      .catch((error) => Sentry.captureException(error, { extra: { challengeId: card.challengeId } }));
    return () => {
      isCurrent = false;
    };
  }, [card, filteredPhoto]);

  const addPhoto = useCallback(async (file) => {
    setPhoto(await loadPhoto(file));
  }, []);

  return {
    addPhoto,
    hasPhoto: !!photo,
    frameId,
    setFrameId,
    filterId,
    setFilterId,
    filterPreviews,
    framePreviews,
    stickerUrl: story.stickerUrl,
    storyFile: story.file,
    storyUrl: story.url,
    isStoryReady: !!story.file && !isRendering,
  };
};

export default useStoryComposer;
